import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-full sm:w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <span className="inline-block text-xs font-medium text-primary-500 bg-primary-50 px-2 py-0.5 rounded-md mb-2">
            {article.category}
          </span>
          <h3 className="font-heading text-base font-semibold text-foreground-100 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-400 line-clamp-2 mb-3">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-heading text-sm font-semibold text-foreground-100 group-hover:text-primary-500 transition-colors line-clamp-2 mb-1">
            {article.title}
          </h4>
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span>·</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-lg overflow-hidden bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="p-4">
        <span className="inline-block text-xs font-medium text-primary-500 bg-primary-50 px-2 py-0.5 rounded-md mb-3">
          {article.category}
        </span>
        <h3 className="font-heading text-base font-semibold text-foreground-100 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-400 line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="flex items-center gap-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {article.date}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {article.readTime}
          </span>
        </div>
      </div>
    </Link>
  );
}